import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  ArrowLeft,
  Filter,
  ChevronDown,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { productAPI } from '../api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "Hair Care",
  "Skin Care",
  "Baked Goods",
  "Gourmet Chocolates",
  "Wine Collection",
  "Holiday Décor",
  "Garden Décor"
];

const AdminProducts = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const data = await productAPI.getAll(params);
      setProducts(data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch products",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchProducts();
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await productAPI.delete(productId);
      toast({
        title: "Success",
        description: "Product deleted successfully"
      });
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete product",
        variant: "destructive"
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      toast({
        title: "No selection",
        description: "Please select products to delete",
        variant: "destructive"
      });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedProducts.length} products?`)) {
      return;
    }

    try {
      setBulkDeleteLoading(true);
      await productAPI.bulkDelete(selectedProducts);
      toast({
        title: "Success",
        description: `${selectedProducts.length} products deleted successfully`
      });
      setSelectedProducts([]);
      fetchProducts();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete products",
        variant: "destructive"
      });
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      }
      return [...prev, productId];
    });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="admin-products-container">
      {/* Header */}
      <div className="admin-header">
        <div className="header-content">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin')}
            className="back-btn"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="page-title">
            <Package className="mr-3" size={32} />
            Product Management
          </h1>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="admin-content">
        <Card className="filters-card">
          <CardContent className="filters-content">
            <div className="filters-row">
              {/* Search */}
              <div className="search-container">
                <Search className="search-icon" size={20} />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      fetchProducts();
                    }}
                    className="clear-search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="category-select">
                  <Filter size={16} className="mr-2" />
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Search Button */}
              <Button onClick={handleSearch} className="search-btn">
                <Search size={18} className="mr-2" />
                Search
              </Button>

              {/* Add Product Button */}
              <Button 
                onClick={() => navigate('/admin/products/new')}
                className="add-product-btn"
              >
                <Plus size={18} className="mr-2" />
                Add Product
              </Button>
            </div>

            {/* Bulk Actions */}
            {selectedProducts.length > 0 && (
              <div className="bulk-actions">
                <span className="selected-count">
                  {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
                </span>
                <Button 
                  variant="destructive" 
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                  className="bulk-delete-btn"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete Selected
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Products List */}
        {loading ? (
          <div className="loading-state">
            <p>Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card className="empty-state">
            <CardContent className="empty-content">
              <Package size={64} className="empty-icon" />
              <h3>No products found</h3>
              <p>Start by adding your first product</p>
              <Button 
                onClick={() => navigate('/admin/products/new')}
                className="mt-4"
              >
                <Plus size={18} className="mr-2" />
                Add Product
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="products-grid">
            {/* Select All Header */}
            <Card className="select-all-card">
              <CardContent className="select-all-content">
                <div className="select-all-checkbox">
                  <Checkbox
                    checked={selectedProducts.length === products.length && products.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                  <span>Select All ({products.length})</span>
                </div>
              </CardContent>
            </Card>

            {/* Product Cards */}
            {filteredProducts.map(product => (
              <Card key={product.id} className="product-card">
                <CardContent className="product-card-content">
                  <div className="product-card-header">
                    <Checkbox
                      checked={selectedProducts.includes(product.id)}
                      onCheckedChange={() => toggleProductSelection(product.id)}
                    />
                    <span className="product-category">{product.category}</span>
                  </div>

                  {/* Product Image */}
                  <div className="product-image-container">
                    {product.images && product.images.length > 0 ? (
                      <img 
                        src={`${BACKEND_URL}${product.images[0]}`}
                        alt={product.name}
                        className="product-image"
                      />
                    ) : (
                      <div className="product-image-placeholder">
                        <Package size={48} />
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-description">{product.description}</p>
                    <div className="product-meta">
                      <span className="product-price">${product.price.toFixed(2)}</span>
                      <span className="product-stock">Stock: {product.stock}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="product-actions">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                      className="edit-btn"
                    >
                      <Edit size={16} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(product.id)}
                      className="delete-btn"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
